import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { FiMapPin, FiNavigation, FiX, FiSearch, FiArrowLeft } from 'react-icons/fi';
import API from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { getCurrentLocation, isWithinServiceArea } from '../utils/location';
import { STORE_LOCATION, JHAJJAR_BOUNDARY } from '../utils/constants';
import './LocationPicker.css';

const JHAJJAR_PRESETS = [
  { name: 'Main Bazar (Store)', short: 'Main Bazar, Jhajjar', lat: 28.605441, lng: 76.653198 },
  { name: 'Adarsh Nagar', short: 'Adarsh Nagar, Jhajjar', lat: 28.6085, lng: 76.6552 },
  { name: 'Model Town', short: 'Model Town, Jhajjar', lat: 28.6012, lng: 76.6480 },
  { name: 'Sector 6', short: 'Sector 6, Jhajjar', lat: 28.6120, lng: 76.6620 },
  { name: 'Old Bus Stand', short: 'Old Bus Stand, Jhajjar', lat: 28.6045, lng: 76.6585 },
  { name: 'Silani Gate', short: 'Silani Gate, Jhajjar', lat: 28.5995, lng: 76.6560 },
  { name: 'Delhi Gate', short: 'Delhi Gate, Jhajjar', lat: 28.6060, lng: 76.6570 },
  { name: 'Govt Girls School', short: 'Near Govt Girls School, Jhajjar', lat: 28.6070, lng: 76.6540 }
];

const LocationPicker = ({ onClose }) => {
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState('select-location'); // 'select-location' | 'fill-address'
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // Temporary location data for step 2
  const [tempCoords, setTempCoords] = useState(null);
  const [tempAreaName, setTempAreaName] = useState('');

  // Structured address fields
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [receiverName, setReceiverName] = useState('');
  const [alternatePhone, setAlternatePhone] = useState('');

  useEffect(() => {
    if (user?.name && !receiverName) {
      setReceiverName(user.name);
    }
  }, [user, receiverName]);

  // Map Picker States & Refs
  const [selectedMapCoords, setSelectedMapCoords] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const mapInstanceRef = useRef(null);
  const markerInstanceRef = useRef(null);

  // Dynamic Leaflet Loader
  const loadLeaflet = () => {
    return new Promise((resolve) => {
      if (window.L) {
        resolve(window.L);
        return;
      }
      
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
      
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => {
        resolve(window.L);
      };
      document.head.appendChild(script);
    });
  };

  // Initialize Leaflet Map
  const initMapPicker = async () => {
    setMapLoading(true);
    setError('');
    try {
      const L = await loadLeaflet();
      
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerInstanceRef.current = null;
      }

      const map = L.map('map-picker-container').setView([STORE_LOCATION.lat, STORE_LOCATION.lng], 14);
      mapInstanceRef.current = map;

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 18,
        attribution: '© OpenStreetMap'
      }).addTo(map);

      // Add custom style store marker
      const storeIcon = L.divIcon({
        className: 'store-custom-icon',
        html: '<div style="background-color: #6C63FF; width: 14px; height: 14px; border-radius: 50%; border: 3px solid #fff; box-shadow: 0 0 10px rgba(0,0,0,0.5);"></div>',
        iconSize: [20, 20],
        iconAnchor: [10, 10]
      });
      L.marker([STORE_LOCATION.lat, STORE_LOCATION.lng], { icon: storeIcon })
        .addTo(map)
        .bindPopup('Shubham Computers Store')
        .openPopup();

      // Add Jhajjar city boundary polygon
      L.polygon(JHAJJAR_BOUNDARY.map(p => [p.lat, p.lng]), {
        color: '#6C63FF',
        fillColor: '#6C63FF',
        fillOpacity: 0.08
      }).addTo(map);

      const userMarkerIcon = L.divIcon({
        className: 'user-custom-icon',
        html: '<div style="background-color: #ff4d4d; width: 16px; height: 16px; border-radius: 50%; border: 3px solid #fff; box-shadow: 0 0 10px rgba(0,0,0,0.5);"></div>',
        iconSize: [22, 22],
        iconAnchor: [11, 11]
      });

      map.on('click', (e) => {
        const { lat, lng } = e.latlng;
        setSelectedMapCoords({ lat, lng });

        if (markerInstanceRef.current) {
          markerInstanceRef.current.setLatLng([lat, lng]);
        } else {
          markerInstanceRef.current = L.marker([lat, lng], { icon: userMarkerIcon }).addTo(map);
        }
        markerInstanceRef.current.bindPopup('Selected Delivery Location').openPopup();
      });
    } catch (err) {
      console.error('Leaflet load error:', err);
      setError('Could not load interactive map. Please use search or current location options.');
    } finally {
      setMapLoading(false);
    }
  };

  useEffect(() => {
    if (step === 'map-picker') {
      const timer = setTimeout(() => {
        initMapPicker();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerInstanceRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleMapSubmit = async () => {
    if (!selectedMapCoords) {
      setError('Please click on the map to add a delivery pointer.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      if (!isWithinServiceArea(selectedMapCoords.lat, selectedMapCoords.lng)) {
        throw new Error(
          'We only deliver within Jhajjar city. Please select a location inside the highlighted area.'
        );
      }

      const place = await reverseGeocode(selectedMapCoords.lat, selectedMapCoords.lng);
      const label = place?.short || 'Jhajjar Area';
      
      setTempCoords(selectedMapCoords);
      setTempAreaName(label);
      setStep('fill-address');
    } catch (err) {
      const msg = err.message || 'Location error';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Reverse geocode lat/lng → human readable area name
  const reverseGeocode = async (lat, lng) => {
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=16&addressdetails=1`;
      const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
      const data = await res.json();
      if (!data || data.error) return null;
      const a = data.address || {};
      // Build a short human label: neighbourhood / suburb / village, city
      const area = a.neighbourhood || a.suburb || a.village || a.town || a.county || '';
      const city = a.city || a.town || a.village || a.state_district || '';
      const short = [area, city].filter(Boolean).join(', ');
      return { 
        short: short || data.display_name?.split(',')[0] || 'Jhajjar Area', 
        full: data.display_name || 'Jhajjar Area' 
      };
    } catch {
      return null;
    }
  };

  const handleCurrentLocation = async () => {
    setLoading(true);
    setError('');
    try {
      const coords = await getCurrentLocation();
      
      if (!isWithinServiceArea(coords.lat, coords.lng)) {
        throw new Error(
          'Sorry, we only deliver within Jhajjar city.'
        );
      }

      const place = await reverseGeocode(coords.lat, coords.lng);
      const label = place?.short || 'Jhajjar Area';
      
      setTempCoords(coords);
      setTempAreaName(label);
      setStep('fill-address');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to get location';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPreset = (preset) => {
    setTempCoords({ lat: preset.lat, lng: preset.lng });
    setTempAreaName(preset.short);
    setStep('fill-address');
    setError('');
  };

  const geocodeAddress = async (query) => {
    // Nominatim free geocoding — bias results to Jhajjar area
    const encoded = encodeURIComponent(`${query}, Jhajjar, Haryana, India`);
    const url = `https://nominatim.openstreetmap.org/search?q=${encoded}&format=json&limit=1&countrycodes=in`;
    const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
    const data = await res.json();
    if (!data || data.length === 0) return null;
    return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
  };

  const handleManualSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter your area or landmark');
      return;
    }
    setLoading(true);
    setSearching(true);
    setError('');
    try {
      const coords = await geocodeAddress(searchQuery.trim());
      if (!coords) {
        throw new Error('Location not found. Try a different landmark or area in Jhajjar.');
      }

      if (!isWithinServiceArea(coords.lat, coords.lng)) {
        throw new Error('Sorry, we only deliver within Jhajjar city.');
      }

      setTempCoords(coords);
      setTempAreaName(searchQuery.trim());
      setStep('fill-address');
    } catch (err) {
      const msg = err.message || 'Failed to find address';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  // Dynamic Google Maps API loader
  useEffect(() => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (key && !window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, []);

  // Real-time suggestions debouncer (OSM + Google dual autocomplete)
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.length < 3) {
      setSuggestions([]);
      return;
    }
    const delayDebounce = setTimeout(async () => {
      // 1. Try Google Places Autocomplete first
      if (window.google && window.google.maps && window.google.maps.places) {
        try {
          const service = new window.google.maps.places.AutocompleteService();
          service.getPlacePredictions({
            input: searchQuery,
            componentRestrictions: { country: 'in' },
            locationBias: new window.google.maps.LatLng(STORE_LOCATION.lat, STORE_LOCATION.lng),
            radius: 10000
          }, (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              setSuggestions(predictions.map(p => ({
                display: p.description,
                placeId: p.place_id,
                isGoogle: true
              })));
            }
          });
          return;
        } catch (gErr) {
          console.warn('Google Autocomplete failed:', gErr);
        }
      }

      // 2. Fallback to Nominatim OpenStreetMap geocoder
      try {
        const encoded = encodeURIComponent(`${searchQuery}, Jhajjar, Haryana, India`);
        const url = `https://nominatim.openstreetmap.org/search?q=${encoded}&format=json&limit=5&countrycodes=in`;
        const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
        const data = await res.json();
        if (data && Array.isArray(data)) {
          setSuggestions(data.map(item => ({
            display: item.display_name,
            lat: parseFloat(item.lat),
            lng: parseFloat(item.lon),
            isGoogle: false
          })));
        }
      } catch (err) {
        console.warn('Nominatim Autocomplete failed:', err);
      }
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  const handleSelectSuggestion = async (s) => {
    setSearchQuery(s.display);
    setSuggestions([]);
    setLoading(true);
    setSearching(true);
    setError('');
    
    try {
      let coords = null;
      if (s.isGoogle) {
        const geocoder = new window.google.maps.Geocoder();
        const results = await new Promise((resolve, reject) => {
          geocoder.geocode({ placeId: s.placeId }, (results, status) => {
            if (status === 'OK' && results && results[0]) resolve(results);
            else reject(new Error('Google geocoding failed'));
          });
        });
        const loc = results[0].geometry.location;
        coords = { lat: loc.lat(), lng: loc.lng() };
      } else {
        coords = { lat: s.lat, lng: s.lng };
      }
      
      if (!isWithinServiceArea(coords.lat, coords.lng)) {
        throw new Error('Sorry, we only deliver within Jhajjar city.');
      }
      
      setTempCoords(coords);
      setTempAreaName(s.display.split(',')[0]);
      setStep('fill-address');
    } catch (err) {
      const msg = err.message || 'Failed to select location';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const handleSaveAddressDetails = async (e) => {
    if (e) e.preventDefault();
    if (!line1.trim()) {
      setError('Flat/House/Building details (Line 1) are required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await API.put('/users/address', {
        line1: line1.trim(),
        line2: line2.trim(),
        lat: tempCoords.lat,
        lng: tempCoords.lng,
        label: 'Home',
        receiverName: receiverName.trim(),
        alternatePhone: alternatePhone.trim()
      });
      updateUser({ address: res.data.address, savedAddresses: res.data.savedAddresses });
      toast.success('Address saved!');
      onClose();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save address';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal location-modal" onClick={e => e.stopPropagation()}>
        
        {step === 'select-location' ? (
          <>
            <div className="modal-header">
              <h3><FiMapPin /> Set Delivery Location</h3>
              <button className="modal-close" onClick={onClose}><FiX /></button>
            </div>

            <p className="location-desc">
              We deliver within 3km of our Jhajjar store under 30 mins ⚡
            </p>

            <button
              className="btn btn-primary btn-full location-gps-btn"
              onClick={handleCurrentLocation}
              disabled={loading}
            >
              <FiNavigation style={{ marginRight: 6 }} />
              {loading && !searching ? 'Checking location...' : 'Use Current Location'}
            </button>

            <button
              className="btn btn-outline btn-full location-map-picker-btn"
              onClick={() => { setStep('map-picker'); setSelectedMapCoords(null); }}
              disabled={loading}
              style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%' }}
            >
              <FiMapPin /> Select Location on App
            </button>

            <div className="location-divider">
              <span>OR</span>
            </div>

            <div className="location-search-wrapper" style={{ position: 'relative', width: '100%', zIndex: 1010 }}>
              <div className="location-search-row" style={{ marginBottom: 0 }}>
                <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Enter area, market or landmark in Jhajjar..."
                    value={searchQuery}
                    onChange={e => { setSearchQuery(e.target.value); setError(''); }}
                    onKeyDown={e => e.key === 'Enter' && handleManualSearch()}
                    maxLength={200}
                  />
                </div>
                <button
                  className="location-search-btn"
                  onClick={handleManualSearch}
                  disabled={loading}
                >
                  {searching ? '…' : <FiSearch size={18} />}
                </button>
              </div>
              {suggestions.length > 0 && (
                <ul className="address-suggestions-dropdown">
                  {suggestions.map((s, idx) => (
                    <li key={idx} onClick={() => handleSelectSuggestion(s)}>
                      <FiMapPin className="suggestion-icon" />
                      <span className="suggestion-text">{s.display}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <button
              className="btn btn-secondary btn-full"
              onClick={handleManualSearch}
              disabled={loading}
              style={{ marginTop: 14 }}
            >
              {searching ? 'Searching...' : 'Search Location'}
            </button>

            <div className="jhajjar-presets-wrapper">
              <div className="jhajjar-presets-title">
                <FiMapPin size={12} /> Popular Jhajjar Areas (Quick Pick)
              </div>
              <div className="jhajjar-preset-chips">
                {JHAJJAR_PRESETS.map((preset, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="jhajjar-preset-chip"
                    onClick={() => handleSelectPreset(preset)}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : step === 'map-picker' ? (
          <>
            <div className="modal-header">
              <h3><FiMapPin /> Choose Location on Map</h3>
              <button className="modal-close" onClick={onClose}><FiX /></button>
            </div>

            <p className="location-desc" style={{ marginBottom: 10 }}>
              Click on the map inside the blue 3km service circle to set your delivery pointer 📍
            </p>

            <div className="map-picker-wrapper">
              <div id="map-picker-container" />
              {mapLoading && (
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 12 }}>
                  <span style={{ fontSize: 13, color: '#fff' }}>Loading Map...</span>
                </div>
              )}
            </div>

            <span className="map-picker-hint">
              {selectedMapCoords 
                ? `Selected Coordinates: ${selectedMapCoords.lat.toFixed(5)}, ${selectedMapCoords.lng.toFixed(5)}`
                : '💡 Tip: You can zoom and drag the map to find your exact location.'}
            </span>

            <div className="loc-btn-group">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => { setStep('select-location'); setError(''); }}
                disabled={loading}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                <FiArrowLeft /> Back
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleMapSubmit}
                disabled={loading || !selectedMapCoords}
                style={{ flex: 1 }}
              >
                {loading ? 'Confirming...' : 'Submit Location'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="modal-header">
              <h3><FiMapPin /> Enter Address Details</h3>
              <button className="modal-close" onClick={onClose}><FiX /></button>
            </div>

            <div className="loc-selected-badge">
              <span className="loc-selected-label">📍 Pin Location:</span>
              <span className="loc-selected-val">{tempAreaName}</span>
            </div>

            <form onSubmit={handleSaveAddressDetails} className="loc-address-form">
              <div className="input-group">
                <label className="input-label">Flat / House No. / Building / Floor <span className="chk-addr-required">*</span></label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. House No. 422, Sector 6, near Market"
                  value={line1}
                  onChange={e => { setLine1(e.target.value); setError(''); }}
                  required
                  maxLength={150}
                />
              </div>

              <div className="input-group">
                <label className="input-label">Directions / Landmark (Optional)</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. Opposite State Bank of India, 3rd floor"
                  value={line2}
                  onChange={e => setLine2(e.target.value)}
                  maxLength={150}
                />
              </div>

              <div className="input-group">
                <label className="input-label">Receiver Name (for delivery) <span className="chk-addr-required">*</span></label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. Ramesh Kumar"
                  value={receiverName}
                  onChange={e => { setReceiverName(e.target.value); setError(''); }}
                  required
                  maxLength={100}
                />
              </div>

              <div className="input-group">
                <label className="input-label">Alternate Mobile Number (Optional)</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. 9876543210"
                  value={alternatePhone}
                  onChange={e => setAlternatePhone(e.target.value)}
                  maxLength={10}
                />
              </div>

              <div className="loc-btn-group">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => { setStep('select-location'); setError(''); }}
                  disabled={loading}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <FiArrowLeft /> Back
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  {loading ? 'Saving...' : 'Save & Proceed'}
                </button>
              </div>
            </form>
          </>
        )}

        {error && <p className="location-error">{error}</p>}
      </div>
    </div>
  );
};

export default LocationPicker;
